/**
 * Create Track modal for a cycle-bound track (TrackIndex + cycle checklist).
 *
 * Minimal creation form: name, owner, org scope.
 * Item scope (category × altitude cell grid) is configured after creation on
 * the track's Defaults tab — where item counts and adjacent tracks are visible.
 *
 * Track owner is required before creation; owner is stored on track metadata
 * and told server-side (create_track + trg_notify_track_created, mig 762).
 * The owner starts as the cycle's default track owner when the cycle names one
 * (owner ruling #9, degap_cycles.default_track_owner_id, mig 767).
 * Every caller's onTrackCreated opens the new track (Defaults tab on desktop).
 */

import { useState, useEffect, useMemo } from 'react';
import { Plus, Loader2, X } from 'lucide-react';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useEnterpriseIdentityStore } from '@/stores/prioritization/enterpriseIdentityStore';
import { useSystemUsers } from '@/hooks/useSystemUsers';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { getCurrentWorkspaceId } from '@/lib/supabaseSync';
import { createTrackWithFeedback } from '@/lib/tracks/createTrackFlow';
import { CREATE_TRACK_DIALOG_TITLE, CREATE_TRACK_LABEL } from '@/lib/tracks/vocabulary';
import {
  type CreateTrackInput,
  type Track,
} from '@/types/prioritization/track';

export interface CreateTrackModalProps {
  open: boolean;
  onClose: () => void;
  cycleId: string;
  cycleName: string;
  /** Called after a successful create (store already updated). */
  onTrackCreated?: (track: Track) => void;
}

export function CreateTrackModal({
  open,
  onClose,
  cycleId,
  cycleName,
  onTrackCreated,
}: CreateTrackModalProps) {
  const tracks = useTrackStore((s) => s.tracks);
  const error = useTrackStore((s) => s.error);
  const clearError = useTrackStore((s) => s.clearError);
  const fetchTracksForCycle = useTrackStore((s) => s.fetchTracksForCycle);

  const orgUnitsMap = useEnterpriseIdentityStore((s) => s.orgUnits);
  const fetchOrgUnits = useEnterpriseIdentityStore((s) => s.fetchFromSupabase);
  const orgUnits = useMemo(() => Array.from(orgUnitsMap.values()), [orgUnitsMap]);
  const orgUnitsSorted = useMemo(
    () => [...orgUnits].sort((a, b) => a.depth - b.depth || a.displayName.localeCompare(b.displayName)),
    [orgUnits],
  );

  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const { users: systemUsers, loading: usersLoading } = useSystemUsers({
    workspaceId: currentWorkspaceId || getCurrentWorkspaceId() || undefined,
  });
  const sortedUsers = useMemo(
    () =>
      [...systemUsers].sort((a, b) =>
        (a.name || a.email).localeCompare(b.name || b.email, undefined, { sensitivity: 'base' }),
      ),
    [systemUsers],
  );

  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newTrackName, setNewTrackName] = useState('');
  const [newTrackDescription, setNewTrackDescription] = useState('');
  const [newTrackOwnerUserId, setNewTrackOwnerUserId] = useState('');
  const [newTrackScopeEntityId, setNewTrackScopeEntityId] = useState<string | null>(null);

  const cycleTracks = useMemo(() => tracks.filter((t) => t.cycleId === cycleId), [tracks, cycleId]);
  const defaultTrackOwnerId = useDegapProcessStore(
    (s) => s.cycles.find((c) => c.id === cycleId)?.defaultTrackOwnerId ?? null,
  );

  // Start from the cycle's default track owner (still a member) when the form opens empty.
  useEffect(() => {
    if (!open || newTrackOwnerUserId || !defaultTrackOwnerId) return;
    if (systemUsers.some((u) => u.id === defaultTrackOwnerId)) setNewTrackOwnerUserId(defaultTrackOwnerId);
  }, [open, newTrackOwnerUserId, defaultTrackOwnerId, systemUsers]);

  useEffect(() => {
    if (open && orgUnits.length === 0) fetchOrgUnits();
  }, [open, orgUnits.length, fetchOrgUnits]);

  const resetForm = () => {
    setNewTrackName('');
    setNewTrackDescription('');
    setNewTrackOwnerUserId('');
    setNewTrackScopeEntityId(null);
    setCreateError(null);
    clearError();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleCreateTrack = async () => {
    if (!newTrackName.trim()) {
      setCreateError('Track name is required');
      return;
    }
    if (!newTrackOwnerUserId.trim()) {
      setCreateError('Track owner is required');
      return;
    }

    setIsCreating(true);
    setCreateError(null);

    const selectedOrgUnit = newTrackScopeEntityId
      ? orgUnits.find((ou) => ou.id === newTrackScopeEntityId)
      : null;
    const existingDefaults = cycleTracks.filter((t) => t.scopeType === 'default');
    const scopeTypeToUse = selectedOrgUnit ? 'org_unit' : existingDefaults.length > 0 ? 'custom_group' : 'default';
    const scopeEntityIdToUse = selectedOrgUnit?.id ?? null;
    const scopeLabelToUse =
      selectedOrgUnit?.displayName ?? (scopeTypeToUse === 'custom_group' ? newTrackName.trim() : 'Default');

    const input: CreateTrackInput = {
      cycleId,
      name: newTrackName.trim(),
      description: newTrackDescription.trim() || undefined,
      scopeType: scopeTypeToUse,
      scopeEntityId: scopeEntityIdToUse,
      scopeLabel: scopeLabelToUse,
      ownerUserId: newTrackOwnerUserId.trim(),
    };

    const result = await createTrackWithFeedback(input);

    setIsCreating(false);

    if (result.ok && result.track) {
      await fetchTracksForCycle(cycleId);
      onTrackCreated?.(result.track);
      resetForm();
      onClose();
    } else {
      setCreateError(result.error || 'Failed to create Track');
    }
  };

  if (!open) return null;

  const canSubmit =
    Boolean(newTrackName.trim()) && Boolean(newTrackOwnerUserId.trim()) && !isCreating && !usersLoading;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={() => !isCreating && handleClose()} />
      <div className="relative bg-[var(--bg-raised)] shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-[var(--text-pri)]">{CREATE_TRACK_DIALOG_TITLE}</h3>
            <p className="text-xs text-[var(--text-ter)] mt-0.5">
              Configure item scope and session defaults on the Track after creation.
            </p>
          </div>
          <button
            type="button"
            onClick={() => !isCreating && handleClose()}
            className="text-[var(--text-ter)] hover:text-[var(--text-sec)]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {(createError || error) && (
          <div className="mb-4 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] text-sm">
            {createError || error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Track Name *</label>
            <input
              type="text"
              value={newTrackName}
              onChange={(e) => setNewTrackName(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-blue-500 focus:border-[var(--slate)]"
              placeholder="e.g., Default Track, Engineering Features, Business Initiatives"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Description</label>
            <textarea
              value={newTrackDescription}
              onChange={(e) => setNewTrackDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-blue-500 focus:border-[var(--slate)]"
              placeholder="Optional description of this Track's purpose"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Track Owner *</label>
            <select
              value={newTrackOwnerUserId}
              onChange={(e) => setNewTrackOwnerUserId(e.target.value)}
              disabled={usersLoading}
              className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-blue-500 focus:border-[var(--slate)] disabled:opacity-60"
            >
              <option value="">{usersLoading ? 'Loading members…' : 'Select a track owner…'}</option>
              {sortedUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name || u.email || u.id}
                </option>
              ))}
            </select>
            <p className="text-xs text-[var(--text-ter)] mt-1">
              This person configures Track participants and runs sessions for {cycleName ? `"${cycleName}"` : 'this cycle'}.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Scope (organization hierarchy)</label>
            <select
              value={newTrackScopeEntityId ?? ''}
              onChange={(e) => setNewTrackScopeEntityId(e.target.value || null)}
              className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-3 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-blue-500 focus:border-[var(--slate)]"
            >
              <option value="">Default (no org scope)</option>
              {orgUnitsSorted.map((ou) => (
                <option key={ou.id} value={ou.id}>
                  {ou.depth > 0 ? '\u2014'.repeat(ou.depth) + ' ' : ''}
                  {ou.displayName}
                </option>
              ))}
            </select>
            {orgUnitsSorted.length === 0 && (
              <p className="text-xs text-[var(--text-ter)] mt-1">No org units configured. Add units in Settings to scope Tracks.</p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={() => !isCreating && handleClose()}
            className="px-4 py-2 text-[var(--text-sec)] hover:bg-[var(--hover-tint)]"
            disabled={isCreating}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleCreateTrack()}
            disabled={!canSubmit}
            className="btn btn-primary flex items-center gap-2"
          >
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" />
                {CREATE_TRACK_LABEL}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
